import axios from 'axios';
import { useState, useEffect } from "react";
import { MessageSquare, CheckCircle2, XCircle, Clock, Phone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatLocalTime } from "@/utils/formatLocalTime"; // 🆕 [P1] timezone Québec centralisé

const API = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_LABELS = {
  avertissement: "Avertissement employé",
  contrat_signe_rep: "Contrat signé — Rep",
  contrat_signe_cp: "Contrat signé — CP",
  commande_materiaux: "Commande matériaux",
  lavage_en_route: "Lavage en route",
  bonus: "Bonus mis à jour",
  travaux_termines: "Travaux terminés",
  rapport_lmds: "Rapport LMDS",
  depot_recu: "Dépôt reçu",
  paiement_final: "Paiement final",
  avis_google: "Avis Google",
  rappel_non_payee: "Rappel non payée",
  rappel_non_payee_auto: "Rappel auto non payée",
  test: "Test",
};

const STATUS_CONFIG = {
  envoye: { label: "Envoyé", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
  erreur: { label: "Erreur", icon: XCircle, color: "text-red-600 bg-red-50" },
  pending: { label: "En cours", icon: Clock, color: "text-amber-600 bg-amber-50" },
};

function formatDate(iso) {
  // 🆕 [P1] Branchement formatLocalTime (timezone Québec centralisé)
  return formatLocalTime(iso, { format: 'short' });
}

export function HistoriqueSMS() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tous");
  const [expand, setExpand] = useState(null);

  useEffect(() => { fetchLogs(); }, [filter]);

  async function fetchLogs() {
    setLoading(true);
    try {
      const q = filter !== "tous" ? `&type_sms=${filter}` : "";
      const r = await axios.get(`${API}/api/sms/historique?limit=200${q}`);
      const d = r.data;
      setLogs(d.logs || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }

  const types = ["tous", ...Object.keys(TYPE_LABELS)];
  const envoyesCount = logs.filter(l => l.status === "envoye").length;
  const erreursCount = logs.filter(l => l.status === "erreur").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#1a237e]" />
            Historique SMS
          </h1>
          <p className="text-slate-500 text-sm mt-1">Tous les messages envoyés via Twilio</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg">
            <CheckCircle2 className="w-4 h-4" />
            {envoyesCount} envoyés
          </div>
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-1.5 rounded-lg">
            <XCircle className="w-4 h-4" />
            {erreursCount} erreurs
          </div>
          <button onClick={fetchLogs} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" title="Rafraîchir">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {types.map(t => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            data-testid={`filter-sms-${t}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === t ? "bg-[#1a237e] text-white border-[#1a237e]" : "border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            {t === "tous" ? "Tous" : TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]" /></div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Aucun SMS dans l'historique</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 flex items-center gap-1"><Phone className="w-3 h-3" /> Destinataire</th>
                <th className="px-4 py-3">Message</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const sc = STATUS_CONFIG[log.status] || STATUS_CONFIG.pending;
                const Icon = sc.icon;
                return (
                  <tr key={log.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpand(expand === log.id ? null : log.id)}
                    data-testid={`sms-log-row-${log.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${sc.color}`}>
                        <Icon className="w-3 h-3" />
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{formatDate(log.sent_at)}</td>
                    <td className="px-4 py-3 text-slate-700">{TYPE_LABELS[log.context?.type] || log.context?.type || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{log.to || log.to_raw || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs">
                      <p className="truncate">{log.body}</p>
                      {expand === log.id && (
                        <pre className="mt-2 text-xs whitespace-pre-wrap bg-slate-100 p-2 rounded text-slate-700">{log.body}</pre>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default HistoriqueSMS;
